import React from 'react';
import { Link } from 'react-router-dom';
import { Alert, Badge, Button, Space, Tooltip } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { FormattedDate, FormattedRelativeTime } from 'react-intl';
import selectUnit from 'shared/utils/selectUnit';
import theme from 'shared/constants/theme.json';
import { Module } from 'shared/prisma/types';
import {
  getStaleCollectionModules,
  getActiveCollectionModules,
} from 'shared/utils/collectionActivity';
import { MODULE_LABEL } from 'shared/constants/settings';

function getModuleStatusMessage(module, activity) {
  return (
    <>
      No
      {' '}
      {MODULE_LABEL[module]}
      {' '}
      data since
      {' '}
      <FormattedRelativeTime {...selectUnit(new Date(activity.lastActivityTimestamp))} />
    </>
  );
}

function CollectionActivityStatus({ collectionActivity, activeLicense, dataSourceType }) {
  if (!collectionActivity) {
    return null;
  }

  const staleModules = getStaleCollectionModules(collectionActivity, { activeLicense, dataSourceType });

  if (!staleModules.length) {
    const activeModules = getActiveCollectionModules(collectionActivity, activeLicense);

    if (activeModules.length) {
      return <Badge status="success" text="Receiving data" />;
    }

    return null;
  }

  if (staleModules.length === 1) {
    const { module, subKey, activity } = staleModules[0];

    return (
      <>
        <ExclamationCircleOutlined style={{ color: theme['error-color'], marginRight: 8 }} />
        <span style={{ color: theme['error-color'] }}>
          {getModuleStatusMessage(module, subKey, activity)}
        </span>
      </>
    );
  }

  return (
    <>
      <ExclamationCircleOutlined style={{ color: theme['error-color'], marginRight: 8 }} />
      <span style={{ color: theme['error-color'] }}>
        {staleModules.length}
        {' '}
        products not receiving data
      </span>
    </>
  );
}

function CollectionActivityTooltip({ collectionActivity, activeLicense, dataSourceType }) {
  const staleModules = getStaleCollectionModules(collectionActivity, { activeLicense, dataSourceType });

  if (!staleModules.length) {
    return null;
  }

  return (
    <Space direction="vertical">
      {staleModules.map(({ module, activity }) => (
        <div key={module}>
          <strong>{MODULE_LABEL[module]}</strong>
          {': last received '}
          <FormattedDate
            value={activity.lastActivityTimestamp}
            year="numeric"
            day="numeric"
            month="short"
            hour="numeric"
            minute="numeric"
          />
        </div>
      ))}
    </Space>
  );
}

export function CollectionActivityAlerts({
  collectionActivity,
  activeLicense,
  dataSourceType,
  dataSourceId,
  showScriptInstructions = false,
}) {
  const staleModules = getStaleCollectionModules(collectionActivity, { activeLicense, dataSourceType });

  if (!staleModules.length) {
    return null;
  }

  return staleModules.map(({ module, activity }) => {
    const showScriptLink = showScriptInstructions && module === Module.Analytics;

    return (
      <Alert
        key={module}
        type="warning"
        showIcon
        message={getModuleStatusMessage(module, activity)}
        action={showScriptLink ? (
          <Link to={`/data-sources/${dataSourceId}/settings/script`}>
            <Button>View script instructions</Button>
          </Link>
        ) : undefined}
      />
    );
  });
}

export default function DataSourceCollectionActivityStatus({ collectionActivity, activeLicense, dataSourceType }) {
  if (!collectionActivity) {
    return null;
  }

  return (
    <Tooltip title={<CollectionActivityTooltip collectionActivity={collectionActivity} activeLicense={activeLicense} dataSourceType={dataSourceType} />}>
      <CollectionActivityStatus collectionActivity={collectionActivity} activeLicense={activeLicense} dataSourceType={dataSourceType} />
    </Tooltip>
  );
}
